import { cn } from './cn.js';
import type { Tone } from './data.js';

const TONE_EDGE: Record<Tone, string> = {
  neutral: 'border-border',
  muted: 'border-border',
  scan: 'border-sky-600/30',
  signal: 'border-clay-600/30',
  good: 'border-herb-600/30',
  alarm: 'border-rust-600/30',
};

const TONE_DOT: Record<Tone, string> = {
  neutral: 'bg-ink-ghost',
  muted: 'bg-ink-ghost',
  scan: 'bg-sky-600',
  signal: 'bg-clay-600',
  good: 'bg-herb-600',
  alarm: 'bg-rust-600',
};

export interface PanelProps {
  readonly children: React.ReactNode;
  readonly tone?: Tone;
  readonly className?: string;
}

/** Modern clean white card surface with subtle hairline border and elevation. */
export function Panel({ children, tone = 'neutral', className }: PanelProps): React.ReactElement {
  return (
    <section
      className={cn(
        'overflow-hidden rounded-card border bg-card shadow-card transition-shadow duration-150',
        TONE_EDGE[tone],
        tone === 'neutral' ? '' : 'border-t-2',
        className,
      )}
    >
      {children}
    </section>
  );
}

export interface PanelHeaderProps {
  readonly index?: string;
  readonly title: string;
  readonly hint?: string;
  readonly actions?: React.ReactNode;
  readonly tone?: Tone;
}

/** Card header: clean tone dot, crisp title, optional subtitle and actions. */
export function PanelHeader({
  index,
  title,
  hint,
  actions,
  tone = 'neutral',
}: PanelHeaderProps): React.ReactElement {
  return (
    <header className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-border bg-card px-5 py-3.5">
      <div className="flex items-center gap-2.5 min-w-0">
        <span aria-hidden="true" className={cn('h-2 w-2 rounded-full shrink-0', TONE_DOT[tone])} />
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {index !== undefined && (
              <span className="font-mono text-[10px] font-semibold tracking-wider text-ink-ghost bg-cream px-1.5 py-0.5 rounded">
                {index}
              </span>
            )}
            <h2 className="truncate text-base font-semibold leading-tight text-ink">{title}</h2>
          </div>
          {hint !== undefined && <p className="mt-0.5 text-xs text-ink-soft">{hint}</p>}
        </div>
      </div>
      {actions !== undefined && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

export interface PanelBodyProps {
  readonly children: React.ReactNode;
  readonly className?: string;
}

export function PanelBody({ children, className }: PanelBodyProps): React.ReactElement {
  return <div className={cn('p-5', className)}>{children}</div>;
}

/** Precision section label with a leading dot. */
export function SectionLabel({
  children,
  className,
}: {
  readonly children: React.ReactNode;
  readonly className?: string;
}): React.ReactElement {
  return (
    <h3 className={cn('eyebrow flex items-center gap-2 text-ink-faint', className)}>
      <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-herb-600" />
      {children}
    </h3>
  );
}

/** Subtle hairline divider. */
export function StripeRule({ className }: { readonly className?: string }): React.ReactElement {
  return <div aria-hidden="true" className={cn('border-t border-border w-full my-2', className)} />;
}
